// src/pages/admin/TicketReport.js
import React, { useState, useEffect } from 'react';
import {
  Search, MoreVertical, MessageSquare, X,
  AlertCircle, Clock, CheckCircle
} from 'lucide-react';
import { database, ref, get, update, push, set } from '../../firebase';

const TicketReport = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [openActionMenu, setOpenActionMenu] = useState(null);

  const statusOptions = [
    { value: 'pending', label: 'Pending', icon: Clock, color: 'text-orange-400' },
    { value: 'in-progress', label: 'In Progress', icon: AlertCircle, color: 'text-blue-400' },
    { value: 'completed', label: 'Completed', icon: CheckCircle, color: 'text-green-400' },
  ];
  

  const fetchAllTickets = async () => {
    try {
      setLoading(true);
      const snapshot = await get(ref(database, 'UserTicket'));
      if (!snapshot.exists()) {
        setTickets([]);
        setFilteredTickets([]);
        return;
      }

      const allTickets = [];
      snapshot.forEach((userSnap) => {
        const userId = userSnap.key;
        userSnap.forEach((ticketSnap) => {
          const ticket = ticketSnap.val();
          allTickets.push({
            userId,
            ticketId: ticketSnap.key,
            ...ticket,
            replies: ticket.replies ? Object.values(ticket.replies) : []
          });
        });
      });

      allTickets.sort((a, b) =>
        new Date(b.lastUpdated || b.createdAt) - new Date(a.lastUpdated || a.createdAt)
      );

      setTickets(allTickets);
      setFilteredTickets(allTickets);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTickets();
    const interval = setInterval(fetchAllTickets, 45000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let result = [...tickets];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(t =>
        t.userId?.toLowerCase().includes(term) ||
        t.ticketId?.toLowerCase().includes(term) ||
        t.subject?.toLowerCase().includes(term) ||
        t.description?.toLowerCase().includes(term) ||
        t.problemType?.toLowerCase().includes(term) ||
        t.priority?.toLowerCase().includes(term)
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter(t => (t.status || 'open').toLowerCase() === statusFilter);
    }
    setFilteredTickets(result);
  }, [searchTerm, statusFilter, tickets]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;

    const ticketPath = `UserTicket/${selectedTicket.userId}/${selectedTicket.ticketId}`;
    const newReply = {
      message: replyText.trim(),
      sender: 'admin',
      timestamp: new Date().toISOString()
    };

    try {
      const repliesRef = ref(database, `${ticketPath}/replies`);
      const newReplyRef = push(repliesRef);
      await set(newReplyRef, newReply);

      await update(ref(database, ticketPath), {
        messages: (selectedTicket.messages || 0) + 1,
        lastUpdated: new Date().toISOString()
      });

      setReplyText('');
      await fetchAllTickets();
      setSelectedTicket(null); // ← close modal after successful send
    } catch (err) {
      console.error('Error sending reply:', err);
      alert('Failed to send reply');
    }
  };

  const handleUpdateStatus = async (ticket, newStatus) => {
    if (newStatus === ticket.status) {
      setOpenActionMenu(null);
      return;
    }

    const ticketPath = `UserTicket/${ticket.userId}/${ticket.ticketId}`;

    try {
      await update(ref(database, ticketPath), {
        status: newStatus,
        lastUpdated: new Date().toISOString()
      });
      await fetchAllTickets();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
    setOpenActionMenu(null);
  };

  const getStatusBadge = (status) => {
    const s = (status || 'open').toLowerCase();
    const styles = {
      open: 'bg-orange-600/80 text-white',
      pending: 'bg-yellow-600/80 text-white',
      'in-progress': 'bg-blue-600/80 text-white',
      completed: 'bg-green-600/80 text-white'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[s] || 'bg-gray-600/80'}`}>
        {s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ')}
      </span>
    );
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return iso;
    }
  };

  const shortPreview = (str = '', len = 5) =>
    str.length > len ? str.substring(0, len) + '...' : (str || '—');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="animate-spin h-12 w-12 border-4 border-yellow-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-gray-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-[1600px] mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2">Ticket Management</h1>
        <p className="text-gray-400 mb-6">View and manage all user support tickets</p>

        {/* Filters */}
        <div className="bg-gray-900/70 border border-gray-800 rounded-xl p-5 mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by user, ID, subject, description, problem type, priority..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-yellow-600 focus:outline-none"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-yellow-600 focus:outline-none min-w-[160px]"
          >
            <option value="all">All Status</option>
            {statusOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <button
            onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-yellow-300 transition"
          >
            Clear
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-gray-900/70 border border-gray-800 rounded-xl">
          <table className="w-full text-left table-fixed">
            <thead className="bg-gray-800/80">
              <tr>
                <th className="px-5 py-4 text-yellow-300 w-[12%]">Ticket ID</th>
                <th className="px-5 py-4 text-yellow-300 w-[14%]">User</th>
                <th className="px-5 py-4 text-yellow-300 w-[18%]">Subject</th>
                <th className="px-5 py-4 text-yellow-300 w-[22%]">Description</th>
                <th className="px-5 py-4 text-yellow-300 w-[10%]">Status</th>
                <th className="px-5 py-4 text-yellow-300 w-[10%]">Created</th>
                <th className="px-5 py-4 text-yellow-300 w-[10%]">Last Updated</th>
                <th className="px-5 py-4 text-yellow-300 w-[6%] text-center">Status Action</th>
                <th className="px-5 py-4 text-yellow-300 w-[6%] text-center">Reply</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filteredTickets.map(ticket => (
                <tr key={ticket.ticketId} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-5 py-4 font-mono text-sm break-all">{ticket.ticketId}</td>
                  <td className="px-5 py-4 font-mono text-yellow-300 text-sm break-all">
                    {ticket.userId.slice(0, 12)}...
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="text-gray-200 truncate">
                        {shortPreview(ticket.subject, 5)}
                      </div>
                      {ticket.subject?.length > 5 && (
                        <button
                          onClick={() => setSelectedTicket(ticket)}
                          className="text-yellow-400 text-xs hover:underline self-start"
                        >
                          Show more
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="text-gray-300 line-clamp-2">
                        {shortPreview(ticket.description, 5)}
                      </div>
                      {ticket.description?.length > 5 && (
                        <button
                          onClick={() => setSelectedTicket(ticket)}
                          className="text-yellow-400 text-xs hover:underline self-start flex items-center gap-1 mt-1"
                        >
                          <MessageSquare size={13} /> Show full
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">{getStatusBadge(ticket.status)}</td>
                  <td className="px-5 py-4 text-sm text-gray-400">{formatDate(ticket.createdAt)}</td>
                  <td className="px-5 py-4 text-sm text-gray-400">{formatDate(ticket.lastUpdated)}</td>

                  {/* Status Action - three dots only */}
                  <td className="px-5 py-4 text-center relative">
                    <div className="relative inline-block">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenActionMenu(prev => prev === ticket.ticketId ? null : ticket.ticketId);
                        }}
                        className="p-2 hover:bg-gray-700 rounded-full transition"
                      >
                        <MoreVertical size={20} className="text-yellow-300" />
                      </button>

                      {openActionMenu === ticket.ticketId && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setOpenActionMenu(null)}
                          />
                          <div className="absolute right-0 top-full mt-1 w-48 bg-gray-850 border border-gray-700 rounded-lg shadow-xl z-50 py-1">
                            {statusOptions.map(opt => (
                              <button
                                key={opt.value}
                                onClick={() => handleUpdateStatus(ticket, opt.value)}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-700 flex items-center gap-3 text-sm text-gray-200"
                              >
                                <opt.icon size={16} className={opt.color} />
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </td>

                  {/* Reply - message icon only */}
                  <td className="px-5 py-4 text-center">
                    <button
                      onClick={() => setSelectedTicket(ticket)}
                      className="p-2 hover:bg-gray-700 rounded-full transition"
                      title="View ticket & reply"
                    >
                      <MessageSquare size={20} className="text-yellow-300" />
                    </button>
                  </td>
                </tr>
              ))}

              {filteredTickets.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-16 text-gray-500">
                    No tickets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Modal - closes automatically after send reply */}
        {selectedTicket && (
          <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-800 flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-yellow-300 break-words">
                    {selectedTicket.subject || 'No subject provided'}
                  </h2>
                  <p className="text-gray-400 mt-1.5 text-sm">
                    Ticket ID: <span className="text-yellow-200">{selectedTicket.ticketId}</span>
                    {' • '} User: <span className="text-yellow-200">{selectedTicket.userId}</span>
                  </p>
                </div>
                <button
                  onClick={() => setSelectedTicket(null)}
                  className="p-1 hover:bg-gray-800 rounded-full"
                >
                  <X size={28} className="text-gray-400 hover:text-white" />
                </button>
              </div>

              {/* Ticket Details */}
              <div className="p-6 border-b border-gray-800 bg-gray-950/40">
                <h3 className="text-lg font-semibold text-yellow-300 mb-4">Ticket Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
                  <div>
                    <span className="text-gray-400 block mb-1">Problem Type</span>
                    <span className="text-gray-200 font-medium">{selectedTicket.problemType || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block mb-1">Priority</span>
                    <span className="text-gray-200 font-medium">{selectedTicket.priority || '—'}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block mb-1">Status</span>
                    <div>{getStatusBadge(selectedTicket.status)}</div>
                  </div>
                  <div>
                    <span className="text-gray-400 block mb-1">Messages</span>
                    <span className="text-gray-200 font-medium">{selectedTicket.messages || 0}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block mb-1">Created</span>
                    <span className="text-gray-200">{formatDate(selectedTicket.createdAt)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400 block mb-1">Last Updated</span>
                    <span className="text-gray-200">{formatDate(selectedTicket.lastUpdated)}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="p-6 border-b border-gray-800">
                <h3 className="text-lg font-semibold text-yellow-300 mb-3">Description</h3>
                <div className="bg-gray-800/50 p-5 rounded-xl border border-gray-700 whitespace-pre-wrap leading-relaxed text-gray-200">
                  {selectedTicket.description || 'No description provided.'}
                </div>
              </div>

              {/* Conversation */}
              <div className="p-6 flex-1 overflow-y-auto">
                <h3 className="text-lg font-semibold text-yellow-300 mb-4 flex items-center gap-2">
                  <MessageSquare size={20} />
                  Conversation ({selectedTicket.messages || 0} messages)
                </h3>
                {selectedTicket.replies?.length > 0 ? (
                  selectedTicket.replies.map((reply, i) => (
                    <div
                      key={i}
                      className={`mb-6 p-5 rounded-xl ${
                        reply.sender === 'admin'
                          ? 'bg-blue-950/50 ml-8 border-l-4 border-blue-500'
                          : 'bg-gray-800/70 mr-8 border-l-4 border-yellow-500'
                      }`}
                    >
                      <div className="flex justify-between text-sm text-gray-400 mb-3">
                        <span className="font-semibold">
                          {reply.sender === 'admin' ? 'Admin Support' : 'User'}
                        </span>
                        <span>{formatDate(reply.timestamp)}</span>
                      </div>
                      <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">
                        {reply.message}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-16 bg-gray-950/30 rounded-xl">
                    No messages yet. Start the conversation by sending a reply.
                  </p>
                )}
              </div>

              {/* Reply */}
              <div className="p-6 border-t border-gray-800">
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Type your reply here..."
                  className="w-full p-4 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-yellow-600 focus:outline-none min-h-[120px] resize-none"
                />
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleSendReply}
                    disabled={!replyText.trim()}
                    className={`flex items-center gap-2 px-7 py-3 rounded-lg font-medium transition ${
                      replyText.trim()
                        ? 'bg-yellow-600 hover:bg-yellow-500 text-black'
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <MessageSquare size={18} />
                    Send Reply
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketReport;